import { type ActorRefFrom, assign, sendParent, setup } from "xstate";

import type { PlayerId } from "@/types/branded";
import { Points } from "../swiss/swissUtils";
import type { MatchId } from "../types";
import type { RoundRobinGraph } from "../types/graph";
import type { RoundRobinNode } from "../types/node";
import type { RoundRobinParticipantStats } from "../types/participantStat";

// --- 1. Core & Graph Types (From your implementation) ---
export const roundRobinMatchMachine = setup({
  types: {
    context: {} as {
      node: RoundRobinNode;
      players: [PlayerId | null | undefined, PlayerId | null | undefined];
      winner: PlayerId | "draw" | undefined;
    },
    input: {} as { node: RoundRobinNode },
    events: {} as
      | { type: "ASSIGN_PLAYER"; playerId: PlayerId | null; slot: 0 | 1 }
      | { type: "REPORT_RESULT"; winnerId: PlayerId },
  },
}).createMachine({
  id: "match",
  initial: "waiting",

  context: ({ input }) => ({
    node: input.node,
    players: [undefined, undefined],
    winner: undefined,
  }),

  states: {
    waiting: {
      always: {
        target: "ready",
        guard: ({ context }) => context.players.every((p) => p !== undefined),
      },
      on: {
        ASSIGN_PLAYER: {
          actions: assign({
            players: ({ context, event }) => {
              const newPlayers = [...context.players] as [PlayerId | null, PlayerId | null];
              newPlayers[event.slot] = event.playerId;
              return newPlayers;
            },
          }),
        },
      },
    },
    ready: {
      always: {
        target: "completed",
        guard: ({ context }) => context.players.includes(null),
      },
      on: {
        REPORT_RESULT: {
          actions: assign({ winner: ({ event }) => event.winnerId }),
          target: "completed",
        },
      },
    },
    completed: {
      type: "final",
      entry: sendParent(
        ({ context }) =>
          ({
            type: "MATCH_FINISHED",
            matchId: context.node.id,
            winnerId: context.winner ?? context.players.find((p) => p !== null) ?? "draw",
            players: context.players as [PlayerId, PlayerId],
          }) satisfies RoundRobinMatchFinishedEvent,
      ),
    },
  },
});

export type RoundRobinMatchActorRef = ActorRefFrom<typeof roundRobinMatchMachine>;

export type RoundRobinContext = {
  players: PlayerId[];
  standings: Map<PlayerId, RoundRobinParticipantStats>;
  graph: RoundRobinGraph;
  matchRefs: Map<MatchId, RoundRobinMatchActorRef>;
  currentRound: number;
  pendingMatches: Set<MatchId>;
};

export type RoundRobinMatchFinishedEvent = {
  type: "MATCH_FINISHED";
  matchId: MatchId;
  winnerId: PlayerId | "draw";
  players: [PlayerId, PlayerId];
};

export const roundRobinMachine = setup({
  types: {
    context: {} as RoundRobinContext,
    events: {} as RoundRobinMatchFinishedEvent,
    input: {} as {
      players: PlayerId[];
      initialGraph: RoundRobinGraph;
    },
    output: [] as PlayerId[][],
  },
  actors: { matchActor: roundRobinMatchMachine },
  actions: {
    spawnMatches: assign(({ context, spawn }) => {
      const newRefs = new Map(context.matchRefs);
      const newPending = new Set(context.pendingMatches);

      context.graph.nodes.forEach((node, id) => {
        if (!newRefs.has(id)) {
          const ref = spawn("matchActor", { id, input: { node } });

          newRefs.set(id, ref);
          newPending.add(id);
        }
      });
      return { matchRefs: newRefs, pendingMatches: newPending };
    }),

    applySeeds: ({ context }) => {
      context.graph.edges.forEach((edge) => {
        if (edge.kind === "seed") {
          const targetRef = context.matchRefs.get(edge.to);
          if (targetRef) {
            targetRef.send({ type: "ASSIGN_PLAYER", playerId: edge.player, slot: edge.slot });
          }
        }
      });
    },

    updateStandings: assign(({ context, event }) => {
      const { winnerId, players } = event;

      const newStandings = new Map(context.standings);
      const newPending = new Set(context.pendingMatches);
      newPending.delete(event.matchId);

      players.forEach((p) => {
        if (!newStandings.has(p)) return;

        const stats = newStandings.get(p)!;

        stats.opponents.push(players.find((op) => op !== p)!);

        if (winnerId === "draw") {
          stats.draws++;
          stats.points += Points.Draw;
        } else if (winnerId === p) {
          stats.wins++;
          stats.points += Points.Win;
        } else {
          stats.losses++;
          stats.points += Points.Loss;
        }
      });

      return { standings: newStandings, pendingMatches: newPending };
    }),
  },
  guards: {
    isFinished: ({ context }) => context.pendingMatches.size === 0,
  },
}).createMachine({
  id: "tournamentManager",
  initial: "initializing",

  context: ({ input }) => ({
    players: input.players,
    standings: new Map(
      input.players.map((p) => [
        p,
        {
          id: p,
          wins: 0,
          losses: 0,
          tiebreakers: 0,
          opponents: [],
          draws: 0,
          points: 0,
        } satisfies RoundRobinParticipantStats,
      ]),
    ),
    graph: input.initialGraph,
    matchRefs: new Map(),
    pendingMatches: new Set(),
    currentRound: 1,
  }),
  states: {
    initializing: {
      entry: ["spawnMatches", "applySeeds"],
      always: "playing",
    },
    playing: {
      on: {
        MATCH_FINISHED: {
          actions: ["updateStandings"],
          target: "evaluating",
        },
      },
    },
    evaluating: {
      always: [{ target: "finished", guard: "isFinished" }, { target: "playing" }],
    },
    pairing: {
      entry: ["spawnMatches", "applySeeds"],
      always: "playing",
    },
    finished: {
      type: "final",
    },
  },

  output: ({ context }) => {
    // assign tiers based on results.
    // This is a standardized way to output standings for a round-robin: group players by their win count, then sort each group by tiebreakers.
    const tiersMap = new Map<number, PlayerId[]>();
    context.standings.forEach((stats, playerId) => {
      if (!tiersMap.has(stats.points)) tiersMap.set(stats.points, []);
      tiersMap.get(stats.points)!.push(playerId);
    });

    const sortedTiers = Array.from(tiersMap.entries())
      .sort((a, b) => b[0] - a[0]) // sort by win count descending
      .map(([_, players]) => {
        // sort each tier by tiebreakers (for simplicity, using opponent win count as tiebreaker here)
        return players.sort((p1, p2) => {
          const p1Stats = context.standings.get(p1)!;
          const p2Stats = context.standings.get(p2)!;

          const p1Tiebreaker = p1Stats.opponents.reduce((sum, op) => {
            const opStats = context.standings.get(op);
            return sum + (opStats ? opStats.points : 0);
          }, 0);

          const p2Tiebreaker = p2Stats.opponents.reduce((sum, op) => {
            const opStats = context.standings.get(op);
            return sum + (opStats ? opStats.points : 0);
          }, 0);

          return p2Tiebreaker - p1Tiebreaker; // higher tiebreaker wins
        });
      });

    return sortedTiers;
  },
});
